import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { duration } from "@/format/time";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { TrackLocations } from "@/hooks/use-segment";
import type { EnvironmentMaxLifetimeExceededDetails } from "@/hooks/use-lifetime-enforcement";
import {
    useCreateEnvironment,
    useCreateEnvironmentFromProject,
    useStartEnvironment,
    type PlainEnvironment,
    type UseCreateBlankEnvironmentParamsFromContextUrl,
    type UseCreateEnvironmentParamsFromContextUrl,
} from "@/queries/environment-queries";
import { useProject } from "@/queries/project-queries";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError } from "@/utils/errors";
import webConfig from "@/web-config";
import { Check, ChevronDown, Copy } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/components/podkit/lib/cn";

type EnvironmentLifetimeExceededModalProps = {
    environment: PlainEnvironment;
    open: boolean;
    onClose: () => void;
    /** When true the server rejected the start — "Restart Anyway" is hidden. */
    serverEnforced?: boolean;
    /** Typed details from the server error when the lifetime policy was exceeded. */
    lifetimeDetails?: EnvironmentMaxLifetimeExceededDetails;
};

type DetailEntry = { label: string; value: string };

function buildDetails(
    environment: PlainEnvironment,
    lifetimeDetails?: EnvironmentMaxLifetimeExceededDetails,
): DetailEntry[] {
    const entries: DetailEntry[] = [];

    const name = environment.metadata?.name || environment.id.slice(0, 8);
    entries.push({ label: "Environment", value: `${name} (${environment.id})` });

    if (environment.metadata?.creator?.id) {
        entries.push({ label: "Owner", value: environment.metadata.creator.id });
    }

    if (environment.metadata?.createdAt) {
        const createdDate = new Date(Number(environment.metadata.createdAt.seconds) * 1000);
        entries.push({
            label: "Created",
            value: createdDate.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }),
        });
    }

    const policySeconds = lifetimeDetails?.policyValue ? Number(lifetimeDetails.policyValue.seconds) : undefined;
    if (policySeconds) {
        entries.push({ label: "Policy", value: `maximum environment lifetime (${duration(policySeconds, "long", "coarse")})` });
    }

    if (lifetimeDetails?.expiredAt) {
        entries.push({
            label: "Expired",
            value: lifetimeDetails.expiredAt.toDate().toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
            }),
        });
    }

    return entries;
}

function updateLifetimeUrl(environmentId: string): string {
    return `${webConfig.origin}/settings/environments?search=${encodeURIComponent(environmentId)}#!update-lifetime`;
}

function detailsToCopyText(entries: DetailEntry[], environmentId: string): string {
    const lines = entries.map((e) => `${e.label}: ${e.value}`);
    lines.push("");
    lines.push("To extend this environment's lifetime, ask your organization admin to visit:");
    lines.push(updateLifetimeUrl(environmentId));
    return lines.join("\n");
}

const DetailsPanel: FC<{ environment: PlainEnvironment; lifetimeDetails?: EnvironmentMaxLifetimeExceededDetails }> = ({
    environment,
    lifetimeDetails,
}) => {
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useTemporaryState(false, 2000);
    const entries = useMemo(() => buildDetails(environment, lifetimeDetails), [environment, lifetimeDetails]);
    const copyText = useMemo(() => detailsToCopyText(entries, environment.id), [entries, environment.id]);

    const handleCopy = useCallback(() => {
        void navigator.clipboard.writeText(copyText).then(
            () => setCopied(true),
            () => toast({ title: "Failed to copy to clipboard" }),
        );
    }, [copyText, setCopied, toast]);

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <div className="overflow-hidden rounded-lg border border-border-base">
                <CollapsibleTrigger asChild>
                    <button
                        type="button"
                        className="flex w-full items-center justify-between bg-surface-secondary px-3 py-2 text-sm text-content-secondary hover:text-content-primary"
                        data-tracking-id="toggle-details-environment-lifetime-exceeded-modal"
                    >
                        <span>Details</span>
                        <ChevronDown
                            size={16}
                            className={cn("transition-transform", { "rotate-180": isOpen })}
                        />
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="bg-surface-secondary px-3 pb-2">
                        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                            {entries.map((entry) => (
                                <div key={entry.label} className="contents">
                                    <dt className="text-content-secondary">{entry.label}</dt>
                                    <dd className="truncate text-content-primary">{entry.value}</dd>
                                </div>
                            ))}
                        </dl>
                        <div className="mt-3 border-t border-border-base pt-2 text-sm">
                            <p className="text-content-secondary">
                                Ask your organization admin to extend the lifetime:
                            </p>
                            <a
                                href={updateLifetimeUrl(environment.id)}
                                className="mt-1 block truncate text-content-link hover:underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Update Lifetime
                            </a>
                        </div>
                        <div className="mt-2 flex justify-end">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="gap-1.5 text-content-secondary"
                                onClick={handleCopy}
                                data-tracking-id="copy-details-environment-lifetime-exceeded-modal"
                            >
                                {copied ? (
                                    <Check className="text-content-success" size={14} />
                                ) : (
                                    <Copy size={14} />
                                )}
                                <span>{copied ? "Copied" : "Copy"}</span>
                            </Button>
                        </div>
                    </div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
};

export const EnvironmentLifetimeExceededModal: FC<EnvironmentLifetimeExceededModalProps> = ({
    environment,
    open,
    onClose,
    serverEnforced = false,
    lifetimeDetails,
}) => {
    const { refetch: refetchProject } = useProject(environment?.metadata?.projectId);
    const startEnvironment = useStartEnvironment();
    const createEnvironment = useCreateEnvironment();
    const createEnvironmentFromProject = useCreateEnvironmentFromProject();
    const navigate = useNavigate();
    const { toast } = useToast();

    const handleContinue = useCallback(async () => {
        // Client-side path: retry with skipPolicyCheck
        try {
            await startEnvironment.mutateAsync({ environmentId: environment.id, skipPolicyCheck: true });
        } catch (error) {
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        }
        onClose();
    }, [onClose, startEnvironment, environment.id, toast]);

    const handleCreateNewEnv = useCallback(async () => {
        try {
            let newEnvironment: PlainEnvironment;
            if (environment.metadata?.projectId) {
                const { data: project } = await refetchProject();
                if (!project) {
                    return;
                }

                newEnvironment = await createEnvironmentFromProject.mutateAsync({
                    project,
                    environmentClassId: environment.spec?.machine?.class,
                });
            } else {
                const initializerSpecs = environment.spec?.content?.initializer?.specs;
                let createOptions:
                    | UseCreateEnvironmentParamsFromContextUrl
                    | UseCreateBlankEnvironmentParamsFromContextUrl;
                if (!initializerSpecs?.length) {
                    createOptions = { type: "blank", classID: environment.spec!.machine!.class };
                } else {
                    let contextURL: string;
                    if (initializerSpecs[0].spec.case === "git") {
                        contextURL = initializerSpecs[0].spec.value.remoteUri;
                    } else {
                        contextURL = initializerSpecs[0].spec.value!.url;
                    }
                    createOptions = {
                        type: "contextUrl",
                        classID: environment.spec!.machine!.class,
                        contextURL,
                    };
                }
                newEnvironment = await createEnvironment.mutateAsync(createOptions);
                navigate(getDetailsURL({ environment: newEnvironment }));
            }
        } catch (error) {
            toast({
                title: "Failed to create a new environment",
                description: formatError(error),
            });
        }
        onClose();
    }, [
        onClose,
        environment.metadata?.projectId,
        environment.spec,
        refetchProject,
        createEnvironmentFromProject,
        createEnvironment,
        navigate,
        toast,
    ]);

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md" data-track-location={TrackLocations.EnvironmentLifetimeExceededModal}>
                <DialogHeader>
                    <DialogTitle>Environment Lifetime Exceeded</DialogTitle>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-4">
                    <p>
                        This environment has exceeded your organization&apos;s maximum lifetime policy. You can share
                        the details below with your organization admin to request an extension, or create a new
                        environment.
                    </p>
                    <DetailsPanel environment={environment} lifetimeDetails={lifetimeDetails} />
                </DialogBody>
                <DialogFooter>
                    {!serverEnforced && (
                        <Button
                            variant="outline"
                            onClick={handleContinue}
                            data-tracking-id="restart-anyway-environment-lifetime-exceeded-modal"
                        >
                            Restart Anyway
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        onClick={handleCreateNewEnv}
                        data-tracking-id="create-new-environment-environment-lifetime-exceeded-modal"
                    >
                        Create New Environment
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
